import { useEffect, useState } from "react";
import { useAuth, useUser, useClerk } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useReadState } from "../state/readState.js";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { fetchUserRooms, getBillingPortal } from "../api.js";
import styles from "../styles/components/ProfilePage.module.css";

export function ProfilePage() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { openUserProfile, signOut } = useClerk();
  const navigate = useNavigate();
  const { readIds } = useReadState();
  const { status: billing } = useBillingStatus();
  const [roomCount, setRoomCount] = useState<number | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (isSignedIn) {
      fetchUserRooms()
        .then((rooms) => setRoomCount(rooms.length))
        .catch(() => setRoomCount(0));
    }
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn || !user) return null;

  const displayName = user.fullName || user.username || user.primaryEmailAddress?.emailAddress;
  const role = (user.publicMetadata as { role?: string } | undefined)?.role;
  const roleLabel = role === "admin" || role === "editor" ? "Site Admin" : "User";

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : null;

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/" className={styles.back}>← Feed</Link>
      </nav>

      <div className={styles.inner}>
        <div className={styles.hero}>
          {user.imageUrl && (
            <div className={styles.avatarRing}>
              <img src={user.imageUrl} alt="" className={styles.avatar} />
            </div>
          )}
          <h1 className={styles.name}>{displayName}</h1>
          {user.username && user.fullName && (
            <p className={styles.handle}>~{user.username}</p>
          )}
          {user.primaryEmailAddress && (
            <p className={styles.email}>{user.primaryEmailAddress.emailAddress}</p>
          )}
          <span className={`${styles.roleBadge} ${role === "admin" || role === "editor" ? styles.roleBadge_admin : ""}`}>
            {roleLabel}
          </span>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{readIds.size.toLocaleString()}</span>
            <span className={styles.statLabel}>Items Read</span>
          </div>
          {roomCount !== null && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{roomCount}</span>
              <span className={styles.statLabel}>My Rooms</span>
            </div>
          )}
          {memberSince && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{memberSince}</span>
              <span className={styles.statLabel}>Member Since</span>
            </div>
          )}
        </div>

        {billing && (
          <div className={styles.planSection}>
            <div className={styles.planRow}>
              <span className={styles.planLabel}>Plan</span>
              <span className={`${styles.planBadge} ${styles[`planBadge_${billing.capabilities.plan}`]}`}>
                {billing.capabilities.plan.charAt(0).toUpperCase() + billing.capabilities.plan.slice(1)}
              </span>
            </div>
            {billing.capabilities.plan === "free" ? (
              <Link to="/premium" className={styles.upgradeLink}>Upgrade for custom feeds & more →</Link>
            ) : (
              <button type="button" className={styles.manageLink} onClick={async () => {
                const url = await getBillingPortal().catch(() => null);
                if (url) window.location.href = url;
              }}>
                Manage subscription →
              </button>
            )}
          </div>
        )}

        <div className={styles.section}>
          <Link to="/my-rooms" className={styles.row}>
            <span>My Rooms</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/premium" className={styles.row}>
            <span>Plans & Billing</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <button
            type="button"
            className={styles.row}
            onClick={() => openUserProfile()}
          >
            <span>Manage Account</span>
            <span className={styles.chevron}>›</span>
          </button>
          <button
            type="button"
            className={`${styles.row} ${styles.rowDanger}`}
            onClick={() => signOut(() => navigate({ to: "/" }))}
          >
            Sign Out
          </button>
        </div>

        <div className={styles.section}>
          <Link to="/settings/digest" className={styles.row}>
            <span>Email Digest</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/settings/filters" className={styles.row}>
            <span>Keyword Filters</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/settings/webhooks" className={styles.row}>
            <span>Webhooks</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/settings/rules" className={styles.row}>
            <span>Automation Rules</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/settings/integrations" className={styles.row}>
            <span>Integrations</span>
            <span className={styles.chevron}>›</span>
          </Link>
          <Link to="/team" className={styles.row}>
            <span>Team</span>
            <span className={styles.chevron}>›</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
